import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SubscribeModal from "@/components/SubscribeModal";

const BASE_NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/book", label: "The Book" },
  { to: "/writings", label: "Writings" },
  { to: "/listen", label: "Listen" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [journeyVisible, setJourneyVisible] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  useEffect(() => {
    // Wait for auth resolution; user is null while loading.
    if (user === null) return;
    if (user) {
      setJourneyVisible(true);
      return;
    }
    api
      .get("/journey")
      .then((r) => setJourneyVisible(!!r.data?.is_published))
      .catch(() => setJourneyVisible(false));
  }, [user]);

  const navItems = journeyVisible
    ? [
        BASE_NAV_ITEMS[0],
        BASE_NAV_ITEMS[1],
        { to: "/lucys-journey", label: "Lucy's Journey" },
        ...BASE_NAV_ITEMS.slice(2),
      ]
    : BASE_NAV_ITEMS;

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 backdrop-blur-md bg-paper/80 border-b border-rule"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between py-5">
          <Link
            to="/"
            data-testid="brand-link"
            className="font-serif-display text-2xl tracking-tight text-ink"
          >
            B.&nbsp;Ruff&nbsp;<span className="text-terracotta">Writes</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-10">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide uppercase link-underline transition-colors ${
                      isActive ? "text-terracotta" : "text-ink-soft hover:text-ink"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}

              {user && user.role === "admin" ? (
                <>
                  <NavLink
                    to="/admin"
                    data-testid="nav-admin"
                    className={({ isActive }) =>
                      `text-sm tracking-wide uppercase link-underline ${
                        isActive ? "text-terracotta" : "text-ink-soft hover:text-ink"
                      }`
                    }
                  >
                    Admin
                  </NavLink>
                  <button
                    onClick={logout}
                    data-testid="nav-logout-btn"
                    className="text-sm tracking-wide uppercase text-ink-soft hover:text-terracotta transition-colors"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                !isAdminArea && (
                  <NavLink
                    to="/login"
                    data-testid="nav-login"
                    className="text-sm tracking-wide uppercase text-ink-soft hover:text-ink"
                  >
                    Sign in
                  </NavLink>
                )
              )}
            </nav>

            <button
              onClick={() => setSubOpen(true)}
              data-testid="nav-subscribe-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors"
            >
              <Mail size={14} /> Subscribe
            </button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-ink"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-6 flex flex-col gap-4 border-t border-rule pt-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-wide ${
                    isActive ? "text-terracotta" : "text-ink-soft"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user && user.role === "admin" ? (
              <>
                <NavLink to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-wide text-ink-soft" data-testid="nav-mobile-admin">
                  Admin
                </NavLink>
                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm uppercase tracking-wide text-ink-soft" data-testid="nav-mobile-logout">
                  Sign out
                </button>
              </>
            ) : (
              <NavLink to="/login" onClick={() => setOpen(false)} className="text-sm uppercase tracking-wide text-ink-soft" data-testid="nav-mobile-login">
                Sign in
              </NavLink>
            )}
            <button
              onClick={() => { setOpen(false); setSubOpen(true); }}
              data-testid="nav-mobile-subscribe-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors"
            >
              <Mail size={14} /> Subscribe
            </button>
          </div>
        )}
      </div>
      <SubscribeModal open={subOpen} onClose={() => setSubOpen(false)} />
    </header>
  );
}
